import './wallet-management.css'
import '../../pages/deposit/deposit.css'

import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import TagManager from 'react-gtm-module'
import { useTranslation } from 'react-i18next'
import { Portal } from 'react-portal'

import gift from '../../assets/wallet-management/deposit_gift.webp'
import { COUNTRY } from '../../constants/countries'
import useDimensions from '../../hooks/useDimensions'
import useDragScroll from '../../hooks/useDragScroll'
import type { User } from '../../store/slices/userSlice'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { PaymentChannel } from '../../types/wallet-management'
import { getAdsInfo } from '../../utils/ads-info'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import closeModal from '../../utils/closeModal'
import { getDeviceInfo } from '../../utils/device'
import { numberWithCommas } from '../../utils/number'
import sendEventToNativeApp, { getAdvertisingId, getReportSdkData } from '../../utils/send-event-to-native-app'
import { Backdrop, GlowButton, Loading, Spinner, ToastMessage } from '../common'
import type { Item } from '../common/scroll-selector'
import Icon from '../icon'
import AmountSelector from '../payment-form/amount-selector'

type Promo = Item & {
  content: string
  bonus_amount: number
  total_credit_amount: number
  text: string | null
  multiple: string
}

const DepositModal = () => {
  const { isDesktop } = useDimensions()
  const modal = useModal()
  const { t } = useTranslation('walletManagement')
  const channels = useBoundStore(state => state.payment.paymentChannels)
  const email = useBoundStore(state => state.user.email)
  const setUser = useBoundStore(state => state.user.setUser)
  const toastMessage = useModal(ToastMessage)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const controller = useRef<AbortController | null>(null)
  const {
    scrollRef: paymentScrollRef,
    canScrollLeft,
    canScrollRight,
    hasDragged,
    onMouseDown,
    onMouseMove,
    onMouseUp,
  } = useDragScroll([channels])

  const [selectedPaymentChannel, setSelectedPaymentChannel] =
    useState<PaymentChannel | null>(channels[0] ?? null)
  const [amount, setAmount] = useState<string>(
    channels[0]?.default_fixed_amount
      ? numberWithCommas(Number(channels[0].default_fixed_amount))
      : '',
  )
  const [selectedPromo, setSelectedPromo] = useState<Promo | null>(null)
  const [promoList, setPromoList] = useState<Promo[]>([])
  const [promoLoading, setPromoLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const amountValue = useMemo(
    () => amount.replaceAll(',', '').replace(/[^0-9.]/g, ''),
    [amount],
  )

  const isCrypto = useMemo(() => {
    return [369, 370].includes(Number(selectedPaymentChannel?.id))
  }, [selectedPaymentChannel])

  const isAmountOutOfRange = useMemo(() => {
    if (!selectedPaymentChannel || !amountValue) return false
    const min = selectedPaymentChannel.pay_limit.minimum
    const max = selectedPaymentChannel.pay_limit.maximum
    if (min && +amountValue < +min) return true
    if (max && +amountValue > +max) return true
    return false
  }, [amountValue, selectedPaymentChannel])

  // Auto-select first channel when channels load
  useEffect(() => {
    if (channels.length && !selectedPaymentChannel) {
      const channel = channels[0]
      setSelectedPaymentChannel(channel)
      if (channel.default_fixed_amount) {
        setAmount(numberWithCommas(Number(channel.default_fixed_amount)))
      }
    }
  }, [channels])

  useEffect(() => {
    if (isDesktop && hasSubmitted && iframeRef.current) {
      handleIframeStyle('inactive')
      setHasSubmitted(false)
    }
  }, [selectedPaymentChannel])

  useEffect(() => {
    if (controller.current) {
      controller.current.abort()
      controller.current = null
    }
    controller.current = new AbortController()

    const fetchPromoList = async () => {
      setPromoLoading(true)
      const response = await api.post<
        ApiResponse<Array<Omit<Promo, 'label'> & { title: string }>>
      >(
        '/payment.php',
        {
          type: 'autopromotion_list',
          amount: amountValue,
          channel_id: selectedPaymentChannel?.id,
        },
        {
          signal: controller.current?.signal,
        },
      )
      const { status, info } = response.data

      if (status === 1) {
        const formatInfo = info.map(item => ({
          label: item.title,
          ...item,
        }))
        if (selectedPromo) {
          const samePromo = formatInfo.find(
            promo => selectedPromo.id === promo.id && !promo.disabled,
          )
          setSelectedPromo(samePromo ?? null)
        } else {
          setSelectedPromo(null)
        }
        setPromoList(formatInfo)
      } else {
        setSelectedPromo(null)
        setPromoList([])
      }
      setPromoLoading(false)
    }
    fetchPromoList()

    return () => {
      if (controller.current) {
        controller.current.abort()
        controller.current = null
      }
    }
  }, [amountValue, selectedPaymentChannel])

  useEffect(() => {
    const messageHandler = (message: MessageEvent<string>) => {
      try {
        const data = JSON.parse(message.data) as Record<'type', unknown>
        if (data?.type === 'back' && iframeRef.current) {
          handleIframeStyle('inactive')
          setHasSubmitted(false)
        }
      } catch (error) {
        // do nothing
      }
    }

    window.addEventListener('message', messageHandler)
    return () => {
      window.removeEventListener('message', messageHandler)
    }
  }, [])

  useEffect(() => {
    if (isCrypto) onSubmit()
  }, [isCrypto])

  const onSubmit = async () => {
    if (!selectedPaymentChannel) return

    const isExternalLink =
      selectedPaymentChannel.is_external_link === 1

    const reqBody = {
      type: 'submitpay',
      amount: amountValue,
      pay_type: 1,
      line_type: selectedPaymentChannel.line_type,
      autopromo: selectedPromo?.id ?? 0,
      pay_id: selectedPaymentChannel.id,
      email: email,
      meta: {
        ...getDeviceInfo(),
        ...getAdsInfo(),
        ...getAdvertisingId(),
        ...getReportSdkData(),
      },
    }

    setIsLoading(true)
    const response = await api.post<
      ApiResponse<{ form: string; message?: string; data: { billno: string } }>
    >('/payment.php', reqBody)

    const { status, info } = response.data

    if (status === 1 || status === 2) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'begin_checkout',
          email,
          transaction_id: info.data.billno,
          value: +amountValue,
          currency: COUNTRY.currency,
          items: [
            {
              item_id: selectedPaymentChannel.id,
              item_name: selectedPaymentChannel.show_name,
              price: +amountValue,
              quantity: '1',
              currency: COUNTRY.currency,
            },
          ],
        },
      })
      sendEventToNativeApp('begin_checkout', {
        email,
        transaction_id: info.data.billno,
        value: +amountValue,
        currency: COUNTRY.currency,
        items: [
          {
            item_id: selectedPaymentChannel.id,
            item_name: selectedPaymentChannel.show_name,
            price: +amountValue,
            quantity: '1',
            currency: COUNTRY.currency,
          },
        ],
      })

      fbq?.('track', 'InitiateCheckout', {
        value: Number(amountValue),
        currency: COUNTRY.currency,
      })

      if (selectedPaymentChannel.need_bank_card === 1) {
        setUser({
          pending_bind: 0,
          pending_amount: amountValue.toString(),
        } as User)
      }

      if (isExternalLink) {
        document.body.innerHTML = info.form
        const form = document.getElementById('pay_form') as HTMLFormElement | null
        form?.submit()
        setIsLoading(false)
      } else if (iframeRef.current) {
        handleIframeStyle('active')
        iframeRef.current.srcdoc = info.form
        const newForm = iframeRef.current.contentDocument?.getElementById(
          'pay_form',
        ) as HTMLFormElement
        newForm?.submit()
        setHasSubmitted(true)
      }
    } else {
      if (typeof info === 'string')
        toastMessage.show({
          text: info,
          isValid: false,
        })
      setIsLoading(false)
    }
  }

  const handleIframeStyle = (status: 'active' | 'inactive') => {
    if (iframeRef?.current)
      if (status === 'active') {
        iframeRef.current.className =
          'static pointer-events-auto w-full h-[calc(100dvh_-_60px)]'
      } else {
        iframeRef.current.className =
          'pointer-events-none absolute w-0 h-0 border-[none] ring-0'
        iframeRef.current.srcdoc = ''
      }
  }

  const handleBack = () => {
    setSelectedPaymentChannel(channels[0])
    handleIframeStyle('inactive')
    setHasSubmitted(false)
  }

  return (
    <Portal>
      {isDesktop && (
        <Backdrop
          zIndex={203}
          isVisible={modal.visible}
          onClick={() => closeModal(modal)}
          aria-label="Close deposit modal"
        />
      )}
      <div
        id="deposit-modal"
        className="deposit-modal-container w-full h-dvh max-w-[600px] bg-primary dark:bg-primary-bg dark:bg-size-primary-bg z-[204] fixed p-0 rounded-none border-0 left-0 top-0 xl:-translate-x-2/4 xl:-translate-y-2/4 xl:h-[unset] xl:max-h-[880px] xl:overflow-y-auto xl:rounded-xl xl:left-2/4 xl:top-2/4">
        <div className="flex flex-col xl:rounded-t-xl h-10 lg:h-[50px]">
          <div className="flex items-center justify-center relative h-10 xl:h-[50px] dark:bg-[rgba(10,10,10,0.94)]">
            {hasSubmitted && (
              <button
                type="button"
                className="absolute left-0 top-0 flx-ctr bg-highlight px-4 h-full xl:rounded-tl-[10px]"
                onClick={handleBack}
                aria-label="Back to form">
                <Icon
                  className="fill-white w-4 h-4"
                  id="chevron-left"
                  width={13}
                  height={13}
                />
              </button>
            )}
            <span className="font-bold uppercase text-base text-txt xl:text-lg dark:text-[#f6e0a0] dark:tracking-[0.04em] dark:[text-shadow:0_0_12px_rgba(245,197,66,0.18)]">
              {t('deposit')}
            </span>
            <button
              className="absolute right-0 top-0 flx-ctr bg-highlight px-4 h-full xl:rounded-tr-[10px]"
              onClick={() => closeModal(modal)}
              aria-label="Close modal">
              <Icon
                className="fill-white w-3.5 h-3.5"
                id="close"
                width={12}
                height={12}
              />
            </button>
          </div>
          {/* Gold shimmer line */}
          <div
            className="hidden dark:block h-[3px] w-full"
            style={{
              background:
                'linear-gradient(90deg, transparent 5%, #774605 20%, #f5c542 40%, #fff8e0 50%, #f5c542 60%, #774605 80%, transparent 95%)',
            }}
          />
          <div className="hidden dark:block h-px w-full bg-[rgba(245,197,66,0.15)] shadow-[0_4px_12px_rgba(245,197,66,0.10)]" />
        </div>
        <div className="flex-1 w-full max-h-[calc(100dvh_-_40px)] lg:max-h-[calc(100dvh_-_70px)] overflow-y-auto">
          {channels.length > 0 && (
            <>
              <iframe
                className="pointer-events-none absolute w-0 h-0 border-[none] ring-0"
                ref={iframeRef}
                onLoad={() => {
                  setIsLoading(false)
                }}
                allow="clipboard-read self *; clipboard-write self *"
              />
              {((hasSubmitted && isLoading) || (isCrypto && isLoading)) && (
                <Loading />
              )}
              {!hasSubmitted && !isCrypto && (
                <>
                  {/* Payment Methods */}
                  <div className="px-3 xl:px-6">
                    <div className="deposit-panel rounded-lg bg-secondary p-3 mb-3 xl:p-4 xl:mb-4 dark:rounded-xl dark:border dark:border-solid dark:border-[rgba(255,255,255,0.03)] overflow-hidden">
                      <p className="deposit-section-title font-semibold uppercase text-sm text-txt inline-block mb-2 xl:text-base xl:mb-3 dark:text-[#f6d56c] dark:font-bold relative">
                        {t('payment-methods')}
                      </p>
                      <div className="relative">
                        {canScrollLeft && (
                          <div className="absolute pb-3 h-full w-[70px] pointer-events-none transition-all duration-[0.2s] ease-[ease-in-out] -left-8 top-0 flx-ctr pl-0 pr-[30px] animate-arrowAnim z-[1]">
                            <Icon
                              id="chevron-left"
                              width="7"
                              height="14"
                              fill="var(--secondary-txt)"
                            />
                          </div>
                        )}
                        {canScrollRight && (
                          <div className="absolute pb-3 h-full w-[70px] pointer-events-none transition-all duration-[0.2s] ease-[ease-in-out] -right-7 top-0 flx-ctr pl-[30px] animate-arrowAnim z-[1]">
                            <Icon
                              id="chevron-right"
                              width="7"
                              height="14"
                              fill="var(--secondary-txt)"
                            />
                          </div>
                        )}
                        <div
                          ref={paymentScrollRef}
                          className="payment-methods-container flex gap-2 overflow-x-auto pt-2 pb-2 -mt-2 xl:gap-3 cursor-grab select-none"
                          onMouseDown={onMouseDown}
                          onMouseMove={onMouseMove}
                          onMouseUp={onMouseUp}
                          onMouseLeave={onMouseUp}>
                          {channels.map(channel => (
                            <button
                              key={channel.id}
                              onClick={() => {
                                if (hasDragged.current) return
                                setSelectedPaymentChannel(channel)
                                setAmount(
                                  channel.default_fixed_amount
                                    ? numberWithCommas(
                                        Number(channel.default_fixed_amount),
                                      )
                                    : '',
                                )
                              }}
                              className={classNames(
                                'relative rounded-lg border-2 border-solid px-1 py-2 flex flex-col items-center gap-1 transition-all shrink-0 min-w-[40%] xl:min-w-[40%] xl:py-3 xl:gap-2 dark:bg-[#1a1a1a] dark:border dark:border-solid dark:border-[rgba(255,255,255,0.08)] dark:rounded-lg',
                                selectedPaymentChannel?.id === channel.id
                                  ? 'border-highlight deposit-payment-active dark:!border-[#f5c542] dark:text-[#f6e0a0]'
                                  : 'border-secondary-txt-20 bg-secondary',
                              )}>
                              {channel.is_recommended && (
                                <span className="absolute -top-2 right-0 bg-highlight text-white text-[7px] xl:text-[9px] font-bold px-1 py-0.5 rounded-t leading-none whitespace-nowrap">
                                  {channel.is_recommended}
                                </span>
                              )}
                              <img
                                className="max-w-[70px] xl:max-w-[100px] object-contain"
                                src={channel.img}
                                alt={channel.show_name}
                              />
                              <span className="text-xs xl:text-sm text-secondary-txt text-center leading-tight">
                                <span className="font-medium text-txt">
                                  {channel.pay_limit.minimum
                                    ? numberWithCommas(
                                        channel.pay_limit.minimum,
                                      )
                                    : ''}
                                </span>
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Amount Section */}
                  {selectedPaymentChannel && (
                    <div>
                      <div className="px-3 xl:px-6">
                        <div className="deposit-panel rounded-lg bg-secondary p-3 xl:p-4 dark:rounded-xl dark:border dark:border-solid dark:border-[rgba(255,255,255,0.03)] overflow-hidden">
                          <p className="text-sm text-secondary-txt mb-2 xl:mb-3 xl:text-base">
                            {[
                              selectedPaymentChannel.pay_limit.minimum
                                ? numberWithCommas(
                                    selectedPaymentChannel.pay_limit.minimum,
                                  )
                                : null,
                              selectedPaymentChannel.pay_limit.maximum
                                ? numberWithCommas(
                                    selectedPaymentChannel.pay_limit.maximum,
                                  )
                                : null,
                            ]
                              .filter(Boolean)
                              .join('-')}{' '}
                            {COUNTRY.currency}
                          </p>
                          <div className="flex rounded-md mb-2 px-3 py-2 border-secondary-txt-20 border bg-primary focus-within:border-highlight transition-colors xl:py-3 dark:rounded-lg dark:border-solid dark:border-[rgba(245,197,66,0.22)] dark:bg-[#0f1012] dark:text-[#fff5d3] dark:focus-within:border-[#f5c542] dark:focus-within:shadow-[0_0_12px_rgba(245,197,66,0.12)]">
                            <input
                              className="flex-1 text-base text-txt bg-transparent"
                              inputMode="numeric"
                              value={amount}
                              onChange={e => {
                                const withoutComma = e.target.value
                                  .replaceAll(',', '')
                                  .replace(/[^0-9.]/g, '')
                                if (/^\d*$/.test(withoutComma)) {
                                  setAmount(numberWithCommas(withoutComma))
                                }
                              }}
                              placeholder="Ingresar cantidad aqui"
                              onFocus={e => {
                                setTimeout(() => {
                                  e.target.selectionStart =
                                    e.target.selectionEnd = 10000
                                }, 0)
                              }}
                              onBlur={() => {
                                const min =
                                  selectedPaymentChannel?.pay_limit.minimum
                                if (min && +amountValue < +min) {
                                  setAmount(numberWithCommas(Number(min)))
                                }
                              }}
                            />
                          </div>
                          <AmountSelector
                            amount={amount}
                            setAmount={setAmount}
                            amounts={selectedPaymentChannel.fixed_amount}
                          />

                          {!!promoList.length && (
                            <div className="mt-2">
                              <p className="deposit-section-title font-semibold uppercase text-sm text-txt inline-block xl:text-base dark:text-[#f6d56c] dark:font-bold relative">
                                {t('bonus-available')}
                              </p>
                              <div className="promo-list-container flex gap-3 overflow-x-auto py-2">
                                {promoLoading
                                  ? Array.from(Array(3).keys()).map(i => (
                                      <div
                                        key={i}
                                        className="animate-pulse duration-[200] min-w-[40%] max-w-[40%] h-[55px] flex items-center relative px-2 py-2 rounded-lg bg-tertiary xl:min-w-[160px] xl:max-w-[160px] xl:h-[70px] xl:px-3 xl:py-4"></div>
                                    ))
                                  : promoList.map(promo => (
                                      <button
                                        className={classNames(
                                          selectedPromo?.id === promo.id
                                            ? 'deposit-bonus-active bg-highlight shadow-[0px_0px_5px_2px_rgba(0,_166,_255,_0.5)] dark:!bg-transparent dark:!border-[#f5c542] dark:!shadow-[0_0_16px_rgba(245,197,66,0.14)]'
                                            : 'bg-tertiary dark:bg-[#252525]',
                                          'min-w-[40%] max-w-[40%] h-[55px] flex items-center relative px-2 py-2 rounded-lg shrink-0 xl:min-w-[160px] xl:max-w-[160px] xl:h-[70px] xl:px-3 xl:py-4 dark:rounded-lg dark:border dark:border-solid dark:border-[rgba(255,255,255,0.08)] dark:transition-all dark:duration-200',
                                        )}
                                        key={promo.id}
                                        onClick={() => {
                                          if (
                                            promo.disabled &&
                                            promo.disabled_reason
                                          ) {
                                            return toastMessage.show({
                                              text: promo.disabled_reason,
                                              isValid: false,
                                            })
                                          }
                                          setSelectedPromo(
                                            selectedPromo?.id === promo.id
                                              ? null
                                              : promo,
                                          )
                                        }}>
                                        <p
                                          className={classNames(
                                            selectedPromo?.id === promo.id
                                              ? 'text-white'
                                              : 'text-txt',
                                            'promo-name text-xs font-bold w-full overflow-hidden text-ellipsis text-left xl:text-sm',
                                          )}>
                                          {promo.label}
                                        </p>
                                        <img
                                          className={classNames(
                                            selectedPromo?.id === promo.id
                                              ? 'opacity-100'
                                              : 'opacity-60',
                                            'w-6 h-6 ml-2 xl:w-8 xl:h-8 xl:ml-3',
                                          )}
                                          src={gift}
                                          alt="gift"
                                        />
                                      </button>
                                    ))}
                              </div>
                            </div>
                          )}

                          <div className="flx-btw-ctr mt-2">
                            <div className="flx-btw-ctr w-full">
                              <label className="mr-3">
                                <span className="text-sm text-secondary-txt xl:text-base">
                                  {t('bonus')}:{' '}
                                </span>
                                <span className="text-base text-highlight font-semibold xl:text-lg dark:text-[#f5c542]">
                                  {selectedPromo
                                    ? `+${numberWithCommas(selectedPromo.bonus_amount)}`
                                    : numberWithCommas(0)}
                                </span>
                              </label>
                              <p className="text-right">
                                <span className="text-sm text-secondary-txt xl:text-base">
                                  {t('total-credit')}:{' '}
                                </span>
                                <span className="text-base text-highlight font-semibold xl:text-lg dark:text-[#f5c542]">
                                  {selectedPromo
                                    ? numberWithCommas(
                                        Number(selectedPromo.bonus_amount) +
                                          Number(amountValue),
                                      )
                                    : numberWithCommas(Number(amountValue))}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="px-3 xl:px-6 pb-6 xl:pb-12">
                        <div className="mt-3 xl:mt-4">
                          {isLoading ? (
                            <Spinner />
                          ) : (
                            <div className="game-card-glow inline-flex w-full rounded-[10px] before:!left-0 before:!top-0 before:!w-full before:!h-full before:!p-0.5">
                              <GlowButton
                                className="w-full h-[50px] rounded-xl text-base xl:h-[56px] xl:text-lg dark:rounded-lg"
                                disabled={
                                  !Number(amountValue) || isAmountOutOfRange
                                }
                                onClick={() => onSubmit()}>
                                {t('deposit')}
                              </GlowButton>
                            </div>
                          )}
                          <p className="text-[10px] xl:text-xs text-secondary-txt text-center mt-3">
                            🛡 Pagos procesados en {COUNTRY.name} • Canales
                            locales autorizados
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </Portal>
  )
}

export default NiceModal.create(DepositModal)
